import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Alert as SnackAlert,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ErrorState, LoadingState, PageContainer } from '@/shared/ui';

import {
  useActivatePromotion,
  useArchivePromotion,
  useCampaigns,
  useCoupons,
  useCreatePromotion,
  useEndPromotion,
  usePausePromotion,
  usePromoCustomerGroups,
  usePromoSalesChannels,
  usePromotion,
  useProductSets,
  useSubmitPromotion,
  useUpdatePromotion,
} from '../../hooks';
import {
  conditionsToRoot,
  createEmptyRoot,
  isTreeValid,
  rootToConditions,
} from '../../lib/conditionTree';
import type { ConditionTreeGroup } from '../../lib/conditionTree';
import { toIsoOrUndefined, toLocalInputValue } from '../../lib/datetimeLocal';
import { usePromoPermissions } from '../../lib/usePromoPermissions';
import { useToast } from '../../lib/useToast';
import { PROMOTION_STATUS_COLOR } from '../../model/statuses';
import type {
  PeriodLimit,
  PromotionAction,
  PromotionExclusivity,
  PromotionRequest,
  PromotionType,
} from '../../model/types';
import { ActionListEditor } from '../components/ActionListEditor';
import { CodeNameMultiSelect } from '../components/CodeNameMultiSelect';
import { ConditionTreeEditor } from '../components/ConditionTreeEditor';
import { CouponFormDrawer } from '../components/CouponFormDrawer';
import { PromotionStatsPanel } from '../components/PromotionStatsPanel';
import { PromotionSummaryRail } from '../components/PromotionSummaryRail';

interface BuilderDraft {
  code: string;
  name: string;
  description: string;
  campaignId: string;
  type: PromotionType;
  validFrom: string;
  validTo: string;
  channelCodes: string[];
  customerGroupCodes: string[];
  priority: string;
  exclusivity: PromotionExclusivity;
  stackingGroupCode: string;
  combinesWithProduct: boolean;
  combinesWithOrder: boolean;
  combinesWithShipping: boolean;
  requiresCoupon: boolean;
  maxApplicationsPerOrder: string;
  perCustomerLimit: string;
  totalRedemptionLimit: string;
  periodLimitCount: string;
  periodLimitPeriod: PeriodLimit['period'];
  conditionRoot: ConditionTreeGroup;
  actions: PromotionAction[];
}

function emptyDraft(campaignId?: string | null): BuilderDraft {
  return {
    code: '',
    name: '',
    description: '',
    campaignId: campaignId ?? '',
    type: 'PRODUCT',
    validFrom: '',
    validTo: '',
    channelCodes: [],
    customerGroupCodes: [],
    priority: '0',
    exclusivity: 'NONE',
    stackingGroupCode: '',
    combinesWithProduct: true,
    combinesWithOrder: true,
    combinesWithShipping: true,
    requiresCoupon: false,
    maxApplicationsPerOrder: '',
    perCustomerLimit: '',
    totalRedemptionLimit: '',
    periodLimitCount: '',
    periodLimitPeriod: 'MONTH',
    conditionRoot: createEmptyRoot(),
    actions: [],
  };
}

function seedFromResponse(promotion: PromotionRequest & { currentVersion?: number }): BuilderDraft {
  return {
    code: promotion.code,
    name: promotion.name,
    description: promotion.description ?? '',
    campaignId: promotion.campaignId ?? '',
    type: promotion.type,
    validFrom: toLocalInputValue(promotion.validFrom),
    validTo: toLocalInputValue(promotion.validTo),
    channelCodes: promotion.channelCodes ?? [],
    customerGroupCodes: promotion.customerGroupCodes ?? [],
    priority: String(promotion.priority ?? 0),
    exclusivity: promotion.exclusivity ?? 'NONE',
    stackingGroupCode: promotion.stackingGroupCode ?? '',
    combinesWithProduct: promotion.combinesWithProduct ?? true,
    combinesWithOrder: promotion.combinesWithOrder ?? true,
    combinesWithShipping: promotion.combinesWithShipping ?? true,
    requiresCoupon: promotion.requiresCoupon ?? false,
    maxApplicationsPerOrder:
      promotion.maxApplicationsPerOrder != null ? String(promotion.maxApplicationsPerOrder) : '',
    perCustomerLimit: promotion.perCustomerLimit != null ? String(promotion.perCustomerLimit) : '',
    totalRedemptionLimit:
      promotion.totalRedemptionLimit != null ? String(promotion.totalRedemptionLimit) : '',
    periodLimitCount: promotion.perCustomerPeriodLimit?.count != null ? String(promotion.perCustomerPeriodLimit.count) : '',
    periodLimitPeriod: promotion.perCustomerPeriodLimit?.period ?? 'MONTH',
    conditionRoot: conditionsToRoot(promotion.conditions),
    actions: promotion.actions ?? [],
  };
}

function draftToRequest(draft: BuilderDraft): PromotionRequest {
  return {
    campaignId: draft.campaignId || null,
    code: draft.code.trim(),
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    type: draft.type,
    validFrom: toIsoOrUndefined(draft.validFrom) ?? null,
    validTo: toIsoOrUndefined(draft.validTo) ?? null,
    channelCodes: draft.channelCodes.length ? draft.channelCodes : null,
    customerGroupCodes: draft.customerGroupCodes.length ? draft.customerGroupCodes : null,
    priority: Number(draft.priority) || 0,
    exclusivity: draft.exclusivity,
    stackingGroupCode: draft.stackingGroupCode.trim() || null,
    combinesWithProduct: draft.combinesWithProduct,
    combinesWithOrder: draft.combinesWithOrder,
    combinesWithShipping: draft.combinesWithShipping,
    requiresCoupon: draft.requiresCoupon,
    maxApplicationsPerOrder: draft.maxApplicationsPerOrder.trim() ? Number(draft.maxApplicationsPerOrder) : null,
    perCustomerLimit: draft.perCustomerLimit.trim() ? Number(draft.perCustomerLimit) : null,
    totalRedemptionLimit: draft.totalRedemptionLimit.trim() ? Number(draft.totalRedemptionLimit) : null,
    perCustomerPeriodLimit: draft.periodLimitCount.trim()
      ? { count: Number(draft.periodLimitCount), period: draft.periodLimitPeriod }
      : null,
    conditions: rootToConditions(draft.conditionRoot),
    actions: draft.actions,
  };
}

const STACKING_GROUP_SUGGESTIONS = ['DISCOUNT', 'SHIPPING', 'GIFT', 'LOYALTY', 'COUPON'];

/** "Promotion builder" — author/edit one promotion end-to-end (plan-011 §8.3, the centerpiece). */
export function PromotionBuilderPage() {
  const { t } = useTranslation('promotions');
  const navigate = useNavigate();
  const { promotionId } = useParams<{ promotionId: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(promotionId);
  const { canView, canManage, canApprove } = usePromoPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const promotionQuery = usePromotion(promotionId, { enabled: isEdit && canView });
  const campaigns = useCampaigns({ size: 200 }, { enabled: canView });
  const salesChannels = usePromoSalesChannels({ enabled: canView });
  const customerGroups = usePromoCustomerGroups({ enabled: canView });
  const productSets = useProductSets({ size: 200 }, { enabled: canView });
  const coupons = useCoupons({ size: 200 }, { enabled: canView && isEdit });

  const [draft, setDraft] = useState<BuilderDraft>(() => emptyDraft(searchParams.get('campaignId')));
  const [dirty, setDirty] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmAction, setConfirmAction] = useState<'end' | 'archive' | null>(null);
  const [couponDrawerOpen, setCouponDrawerOpen] = useState(false);

  useEffect(() => {
    if (isEdit && promotionQuery.data) {
      setDraft(seedFromResponse(promotionQuery.data));
      setDirty(false);
    }
  }, [isEdit, promotionQuery.data]);

  const createMutation = useCreatePromotion();
  const updateMutation = useUpdatePromotion();
  const submitMutation = useSubmitPromotion();
  const pauseMutation = usePausePromotion();
  const activateMutation = useActivatePromotion();
  const endMutation = useEndPromotion();
  const archiveMutation = useArchivePromotion();

  const saving = createMutation.isPending || updateMutation.isPending;
  const lifecyclePending =
    submitMutation.isPending ||
    pauseMutation.isPending ||
    activateMutation.isPending ||
    endMutation.isPending ||
    archiveMutation.isPending;

  const promotion = promotionQuery.data;
  const status = promotion?.status ?? 'DRAFT';
  const isExternal = promotion?.source === 'EXTERNAL';
  const isPendingForNonApprover = status === 'PENDING_APPROVAL' && !canApprove;
  const readOnly = !canManage || isExternal || isPendingForNonApprover;

  const existingCoupon = useMemo(
    () => (isEdit && promotionId ? coupons.data?.content.find((c) => c.promotionId === promotionId) : undefined),
    [isEdit, promotionId, coupons.data],
  );

  const hasProductSetCondition = useMemo(() => {
    function scan(g: ConditionTreeGroup): boolean {
      return g.children.some((child) =>
        child.kind === 'leaf'
          ? (child.type === 'PRODUCT_IN_SET' || child.type === 'CATEGORY_IN_SET') && Boolean(child.params.productSetId)
          : scan(child),
      );
    }
    return scan(draft.conditionRoot);
  }, [draft.conditionRoot]);

  const resolveProductSetName = (id: string) => productSets.data?.content.find((s) => s.id === id)?.name;

  if (!canView) {
    return (
      <PageContainer>
        <ErrorState title={t('accessRestricted.title')} message={t('accessRestricted.message')} />
      </PageContainer>
    );
  }

  if (isEdit && promotionQuery.isLoading) {
    return (
      <PageContainer>
        <LoadingState label={t('builder.loading')} fullHeight />
      </PageContainer>
    );
  }
  if (isEdit && promotionQuery.isError) {
    return (
      <PageContainer>
        <ErrorState
          title={t('builder.loadError.title')}
          message={promotionQuery.error?.message ?? t('common:error.unknown')}
          onRetry={() => void promotionQuery.refetch()}
        />
      </PageContainer>
    );
  }

  const update = <K extends keyof BuilderDraft>(key: K, value: BuilderDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setDirty(true);
  };

  const handleSaveDraft = async () => {
    const body = draftToRequest(draft);
    try {
      if (isEdit && promotionId) {
        await updateMutation.mutateAsync({ promotionId, body });
      } else {
        const created = await createMutation.mutateAsync(body);
        success(t('builder.draftSaved'));
        setDirty(false);
        navigate(`/promo/promotions/${created.id}`, { replace: true });
        return;
      }
      success(t('builder.draftSaved'));
      setDirty(false);
    } catch (err) {
      notifyError((err as { message?: string }).message ?? t('builder.saveError'));
    }
  };

  const handleReset = () => {
    if (isEdit && promotion) setDraft(seedFromResponse(promotion));
    else setDraft(emptyDraft(searchParams.get('campaignId')));
    setDirty(false);
  };

  const handleSubmit = async () => {
    if (!promotionId) return;
    try {
      const result = await submitMutation.mutateAsync(promotionId);
      success(
        result.status === 'PENDING_APPROVAL'
          ? t('builder.submittedForApproval')
          : t('builder.submittedActivated'),
      );
    } catch (err) {
      notifyError((err as { message?: string }).message ?? t('builder.saveError'));
    }
  };

  const handleDuplicate = async () => {
    if (!promotion) return;
    try {
      const copy = draftToRequest({ ...draft, code: `${draft.code}-COPY` });
      const created = await createMutation.mutateAsync(copy);
      success(t('builder.duplicated'));
      navigate(`/promo/promotions/${created.id}`);
    } catch (err) {
      notifyError((err as { message?: string }).message ?? t('builder.saveError'));
    }
    setMenuAnchor(null);
  };

  const conditionsValid = isTreeValid(draft.conditionRoot);
  const canSubmit =
    draft.actions.length > 0 && conditionsValid && (!draft.requiresCoupon || Boolean(existingCoupon));

  return (
    <PageContainer>
      <Button
        component={RouterLink}
        to="/promo/promotions"
        startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
        size="small"
        color="inherit"
        sx={{ alignSelf: 'flex-start', color: 'text.secondary', mb: 2 }}
      >
        {t('builder.backLink')}
      </Button>

      <Stack spacing={2.5} sx={{ pb: 4 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" useFlexGap>
        <Typography variant="h5" sx={{ fontWeight: 700, flexShrink: 0 }}>
          {draft.name || t('builder.newPromotionTitle')}
        </Typography>
        {draft.code ? (
          <Chip size="small" label={draft.code} sx={{ fontFamily: 'JetBrains Mono, monospace' }} />
        ) : null}
        {isEdit ? <Chip size="small" color={PROMOTION_STATUS_COLOR[status]} label={status} /> : null}
        {isEdit && promotion ? (
          <Chip size="small" variant="outlined" label={t('builder.versionChip', { version: promotion.currentVersion })} />
        ) : null}

        <Box sx={{ flex: 1 }} />

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PlayCircleOutlineRoundedIcon />}
            component={RouterLink}
            to={promotionId ? `/promo/simulator?promotionId=${promotionId}` : '/promo/simulator'}
          >
            {t('builder.testInSimulator')}
          </Button>

          {!readOnly && isEdit && (status === 'DRAFT' || status === 'REJECTED') ? (
            <Button
              variant="contained"
              size="small"
              disabled={lifecyclePending || !canSubmit}
              onClick={() => void handleSubmit()}
            >
              {t('builder.submitForActivation')}
            </Button>
          ) : null}
          {!readOnly && (status === 'ACTIVE' || status === 'SCHEDULED') ? (
            <Button
              variant="contained"
              size="small"
              color="warning"
              disabled={lifecyclePending || !promotionId}
              onClick={() =>
                promotionId &&
                pauseMutation.mutate(
                  { promotionId },
                  {
                    onSuccess: () => success(t('builder.paused')),
                    onError: (e) => notifyError(e.message),
                  },
                )
              }
            >
              {t('builder.pause')}
            </Button>
          ) : null}
          {!readOnly && status === 'PAUSED' ? (
            <Button
              variant="contained"
              size="small"
              disabled={lifecyclePending || !promotionId}
              onClick={() =>
                promotionId &&
                activateMutation.mutate(promotionId, {
                  onSuccess: () => success(t('builder.resumed')),
                  onError: (e) => notifyError(e.message),
                })
              }
            >
              {t('builder.resume')}
            </Button>
          ) : null}
          {status === 'PENDING_APPROVAL' ? (
            canApprove ? (
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label={t('builder.awaitingApproval')}
                component={RouterLink}
                to="/promo/approvals"
                clickable
              />
            ) : (
              <Chip size="small" color="info" variant="outlined" label={t('builder.awaitingApproval')} />
            )
          ) : null}

          {isEdit ? (
            <>
              <IconButton size="small" aria-label={t('builder.moreActionsAria')} onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <MoreVertRoundedIcon />
              </IconButton>
              <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem onClick={() => void handleDuplicate()}>{t('builder.duplicate')}</MenuItem>
                {!readOnly && (status === 'ACTIVE' || status === 'PAUSED' || status === 'SCHEDULED') ? (
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      setConfirmAction('end');
                    }}
                  >
                    {t('builder.end')}
                  </MenuItem>
                ) : null}
                {!readOnly && status !== 'ARCHIVED' ? (
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      setConfirmAction('archive');
                    }}
                  >
                    {t('builder.archive')}
                  </MenuItem>
                ) : null}
              </Menu>
            </>
          ) : null}
        </Stack>
      </Stack>

      {isExternal ? (
        <Alert severity="info">{t('builder.externalSourceAlert')}</Alert>
      ) : isPendingForNonApprover ? (
        <Alert severity="info">{t('builder.pendingApprovalLockedAlert')}</Alert>
      ) : readOnly ? (
        <Alert severity="info" icon={<LockRoundedIcon fontSize="inherit" />}>
          {t('builder.readOnlyAlert')}
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', gap: 2.5, flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'flex-start' }}>
        <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0, maxWidth: { lg: 760 } }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                {t('builder.basicsCardTitle')}
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={t('builder.codeLabel')}
                    required
                    disabled={readOnly || isEdit}
                    value={draft.code}
                    onChange={(e) => update('code', e.target.value.toUpperCase())}
                    helperText={isEdit ? t('builder.codeLockedHint') : undefined}
                    fullWidth
                  />
                  <TextField
                    label={t('builder.nameLabel')}
                    required
                    disabled={readOnly}
                    value={draft.name}
                    onChange={(e) => update('name', e.target.value)}
                    fullWidth
                  />
                </Stack>
                <TextField
                  label={t('builder.descriptionLabel')}
                  multiline
                  minRows={2}
                  disabled={readOnly}
                  value={draft.description}
                  onChange={(e) => update('description', e.target.value)}
                  fullWidth
                />
                <TextField
                  select
                  label={t('builder.campaignLabel')}
                  disabled={readOnly}
                  value={draft.campaignId}
                  onChange={(e) => update('campaignId', e.target.value)}
                  fullWidth
                >
                  <MenuItem value="">{t('builder.noCampaign')}</MenuItem>
                  {(campaigns.data?.content ?? []).map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t('builder.typeLabel')}
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    disabled={readOnly}
                    value={draft.type}
                    onChange={(_e, next: PromotionType | null) => next && update('type', next)}
                    size="small"
                  >
                    <ToggleButton value="PRODUCT">{t('builder.type.PRODUCT')}</ToggleButton>
                    <ToggleButton value="CART">{t('builder.type.CART')}</ToggleButton>
                    <ToggleButton value="SHIPPING">{t('builder.type.SHIPPING')}</ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {t(`builder.typeHint.${draft.type}`)}
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={t('builder.validFromLabel')}
                    type="datetime-local"
                    disabled={readOnly}
                    value={draft.validFrom}
                    onChange={(e) => update('validFrom', e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    fullWidth
                  />
                  <TextField
                    label={t('builder.validToLabel')}
                    type="datetime-local"
                    disabled={readOnly}
                    value={draft.validTo}
                    onChange={(e) => update('validTo', e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    fullWidth
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <ConditionTreeEditor
            value={draft.conditionRoot}
            onChange={(root) => update('conditionRoot', root)}
            disabled={readOnly}
            onResult={(sev, msg) => (sev === 'success' ? success(msg) : notifyError(msg))}
          />

          <ActionListEditor
            actions={draft.actions}
            onChange={(actions) => update('actions', actions)}
            hasProductSetCondition={hasProductSetCondition}
            disabled={readOnly}
          />

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                {t('builder.restrictionsCardTitle')}
              </Typography>
              <Stack spacing={2}>
                <CodeNameMultiSelect
                  options={salesChannels.data ?? []}
                  loading={salesChannels.isFetching}
                  value={draft.channelCodes}
                  onChange={(channelCodes) => update('channelCodes', channelCodes)}
                  disabled={readOnly}
                  label={t('builder.channelsLabel')}
                  helperText={t('builder.channelsNoneHint')}
                />
                <CodeNameMultiSelect
                  options={customerGroups.data ?? []}
                  loading={customerGroups.isFetching}
                  value={draft.customerGroupCodes}
                  onChange={(customerGroupCodes) => update('customerGroupCodes', customerGroupCodes)}
                  disabled={readOnly}
                  label={t('builder.customerGroupsLabel')}
                  helperText={t('builder.customerGroupsNoneHint')}
                />
                <TextField
                  label={t('builder.priorityLabel')}
                  type="number"
                  disabled={readOnly}
                  value={draft.priority}
                  onChange={(e) => update('priority', e.target.value)}
                  helperText={t('builder.priorityHint')}
                  sx={{ maxWidth: 220 }}
                />

                <Divider />

                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('builder.exclusivityLabel')}
                </Typography>
                <RadioGroup
                  value={draft.exclusivity}
                  onChange={(e) => update('exclusivity', e.target.value as PromotionExclusivity)}
                >
                  <FormControlLabel
                    value="NONE"
                    disabled={readOnly}
                    control={<Radio />}
                    label={t('builder.exclusivity.NONE')}
                  />
                  <FormControlLabel
                    value="STACKING_GROUP"
                    disabled={readOnly}
                    control={<Radio />}
                    label={t('builder.exclusivity.STACKING_GROUP')}
                  />
                  {draft.exclusivity === 'STACKING_GROUP' ? (
                    <Autocomplete
                      freeSolo
                      size="small"
                      disabled={readOnly}
                      options={STACKING_GROUP_SUGGESTIONS}
                      inputValue={draft.stackingGroupCode}
                      onInputChange={(_e, next) => update('stackingGroupCode', next.toUpperCase())}
                      renderInput={(params) => (
                        <TextField {...params} label={t('builder.stackingGroupLabel')} />
                      )}
                      sx={{ maxWidth: 260, ml: 4 }}
                    />
                  ) : null}
                  <FormControlLabel
                    value="GLOBAL"
                    disabled={readOnly}
                    control={<Radio />}
                    label={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <span>{t('builder.exclusivity.GLOBAL')}</span>
                        <WarningAmberRoundedIcon fontSize="inherit" color="warning" />
                      </Stack>
                    }
                  />
                </RadioGroup>

                <Divider />

                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('builder.combinabilityLabel')}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.combinesWithProduct}
                      disabled={readOnly}
                      onChange={(e) => update('combinesWithProduct', e.target.checked)}
                    />
                  }
                  label={t('builder.combinesWithProduct')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.combinesWithOrder}
                      disabled={readOnly}
                      onChange={(e) => update('combinesWithOrder', e.target.checked)}
                    />
                  }
                  label={t('builder.combinesWithOrder')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.combinesWithShipping}
                      disabled={readOnly}
                      onChange={(e) => update('combinesWithShipping', e.target.checked)}
                    />
                  }
                  label={t('builder.combinesWithShipping')}
                />

                <Divider />

                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.requiresCoupon}
                      disabled={readOnly}
                      onChange={(e) => update('requiresCoupon', e.target.checked)}
                    />
                  }
                  label={t('builder.requiresCouponLabel')}
                />

                <Divider />

                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('builder.limitsLabel')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label={t('builder.maxPerOrderLabel')}
                    type="number"
                    disabled={readOnly}
                    value={draft.maxApplicationsPerOrder}
                    onChange={(e) => update('maxApplicationsPerOrder', e.target.value)}
                    helperText={t('common:field.optional')}
                    fullWidth
                  />
                  <TextField
                    label={t('builder.perCustomerLimitLabel')}
                    type="number"
                    disabled={readOnly}
                    value={draft.perCustomerLimit}
                    onChange={(e) => update('perCustomerLimit', e.target.value)}
                    helperText={t('common:field.optional')}
                    fullWidth
                  />
                  <TextField
                    label={t('builder.totalLimitLabel')}
                    type="number"
                    disabled={readOnly}
                    value={draft.totalRedemptionLimit}
                    onChange={(e) => update('totalRedemptionLimit', e.target.value)}
                    helperText={t('common:field.optional')}
                    fullWidth
                  />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label={t('builder.periodLimitCountLabel')}
                    type="number"
                    disabled={readOnly}
                    value={draft.periodLimitCount}
                    onChange={(e) => update('periodLimitCount', e.target.value)}
                    sx={{ width: 160 }}
                  />
                  <TextField
                    select
                    label={t('builder.periodLimitPeriodLabel')}
                    disabled={readOnly || !draft.periodLimitCount.trim()}
                    value={draft.periodLimitPeriod}
                    onChange={(e) => update('periodLimitPeriod', e.target.value as PeriodLimit['period'])}
                    sx={{ width: 160 }}
                  >
                    <MenuItem value="DAY">{t('builder.period.DAY')}</MenuItem>
                    <MenuItem value="WEEK">{t('builder.period.WEEK')}</MenuItem>
                    <MenuItem value="MONTH">{t('builder.period.MONTH')}</MenuItem>
                  </TextField>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {draft.requiresCoupon ? (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  {t('builder.couponCardTitle')}
                </Typography>
                {existingCoupon ? (
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2">
                      {existingCoupon.name || t('builder.unnamedCoupon')}
                    </Typography>
                    <Chip size="small" label={existingCoupon.kind} />
                    <Button
                      size="small"
                      component={RouterLink}
                      to={`/promo/coupons/${existingCoupon.id}`}
                    >
                      {t('builder.openCoupon')}
                    </Button>
                  </Stack>
                ) : (
                  <Alert
                    severity="warning"
                    action={
                      isEdit && !readOnly ? (
                        <Button color="inherit" size="small" onClick={() => setCouponDrawerOpen(true)}>
                          {t('builder.createCoupon')}
                        </Button>
                      ) : undefined
                    }
                  >
                    {isEdit
                      ? t('builder.noCouponYetHint')
                      : t('builder.saveBeforeCouponHint')}
                  </Alert>
                )}
              </CardContent>
            </Card>
          ) : null}
        </Stack>

        <Stack spacing={2.5} sx={{ width: { xs: '100%', lg: 320 }, flexShrink: 0 }}>
          <PromotionSummaryRail
            promotion={{
              type: draft.type,
              actions: draft.actions,
              conditions: rootToConditions(draft.conditionRoot),
              requiresCoupon: draft.requiresCoupon,
              channelCodes: draft.channelCodes,
            }}
            conditionRoot={draft.conditionRoot}
            hasPeriod={Boolean(draft.validFrom && draft.validTo)}
            hasCoupon={Boolean(existingCoupon)}
            promotionId={promotionId}
            resolveProductSetName={resolveProductSetName}
          />
          {/* Stats are only meaningful for an already-saved promotion — a not-yet-created DRAFT has
              no order history to aggregate. */}
          {isEdit && promotionId ? <PromotionStatsPanel promotionId={promotionId} /> : null}
        </Stack>
      </Box>

      {!readOnly ? (
        <Paper
          variant="outlined"
          sx={{
            position: 'sticky',
            bottom: 16,
            p: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            boxShadow: 3,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {dirty ? t('builder.unsavedChanges') : t('builder.allSaved')}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button onClick={handleReset} disabled={saving || !dirty}>
              {t('common:action.reset')}
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
              disabled={saving || !draft.code.trim() || !draft.name.trim()}
              onClick={() => void handleSaveDraft()}
            >
              {t('builder.saveDraft')}
            </Button>
          </Stack>
        </Paper>
      ) : null}
      </Stack>

      <Dialog open={confirmAction === 'end'} onClose={() => setConfirmAction(null)}>
        <DialogTitle>{t('builder.endDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('builder.endDialog.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={lifecyclePending}
            onClick={() => {
              if (!promotionId) return;
              endMutation.mutate(promotionId, {
                onSuccess: () => {
                  success(t('builder.ended'));
                  setConfirmAction(null);
                },
                onError: (e) => notifyError(e.message),
              });
            }}
          >
            {t('builder.end')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmAction === 'archive'} onClose={() => setConfirmAction(null)}>
        <DialogTitle>{t('builder.archiveDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('builder.archiveDialog.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={lifecyclePending}
            onClick={() => {
              if (!promotionId) return;
              archiveMutation.mutate(promotionId, {
                onSuccess: () => {
                  success(t('builder.archived'));
                  setConfirmAction(null);
                },
                onError: (e) => notifyError(e.message),
              });
            }}
          >
            {t('builder.archive')}
          </Button>
        </DialogActions>
      </Dialog>

      <CouponFormDrawer
        open={couponDrawerOpen}
        promotionId={promotionId}
        onClose={() => setCouponDrawerOpen(false)}
        onSaved={() => void coupons.refetch()}
        onResult={(sev, msg) => (sev === 'success' ? success(msg) : notifyError(msg))}
      />

      <Snackbar open={toast !== null} autoHideDuration={5000} onClose={clear} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <SnackAlert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </SnackAlert>
      </Snackbar>
    </PageContainer>
  );
}
